import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAuth } from '../auth/AuthProvider';

function useFinanceAuth() {
  const { user, demo } = useAuth();
  return { user, demo, enabled: Boolean(user) && !demo };
}

function useInvalidateFinance() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['fin'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard-fin'] }),
      queryClient.invalidateQueries({ queryKey: ['rates'] }),
    ]);
}

export function useDashboardFinanciero() {
  const { user, demo } = useAuth();
  const enabled =
    user !== null &&
    !demo &&
    user.rol !== undefined &&
    (['admin', 'supervisor', 'auditor'] as string[]).includes(user.rol);
  return useQuery({
    queryKey: ['dashboard-fin'],
    enabled,
    staleTime: 30_000,
    queryFn: async () => (await api.finanzas.dashboardFinanciero()).data,
  });
}

export function useFacturasFinanzas(filtros: {
  estado?: string;
  desde?: string;
  hasta?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'facturas', filtros],
    enabled,
    queryFn: () => api.finanzas.facturas.list(filtros),
  });
}

export function useFacturaDetalle(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'factura', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.facturas.detail(id!)).data,
  });
}

export function useFacturasPendientes(clienteId?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'facturas-pendientes', clienteId ?? 'todos'],
    enabled: enabled && Boolean(clienteId),
    queryFn: async () => (await api.finanzas.facturas.pendientes(clienteId)).data,
  });
}

export function useCrearFactura() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.facturas.crear(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useAnularFactura() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.facturas.anular(id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useCompras(filtros: {
  estado?: string;
  desde?: string;
  hasta?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'compras', filtros],
    enabled,
    queryFn: () => api.finanzas.compras.list(filtros),
  });
}

export function useCompraDetalle(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'compra', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.compras.detail(id!)).data,
  });
}

export function useComprasPendientes(proveedorId?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'compras-pendientes', proveedorId ?? 'todos'],
    enabled: enabled && Boolean(proveedorId),
    queryFn: async () => (await api.finanzas.compras.pendientes(proveedorId)).data,
  });
}

export function useCrearNotaCompra() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.finanzas.compras.crearNota(id, payload),
    onSuccess: () => void invalidate(),
  });
}

export function useCrearCompra() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.compras.crear(payload),
    onSuccess: () => void invalidate(),
  });
}

export function usePagosProveedor() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'pagos-proveedor'],
    enabled,
    queryFn: async () => (await api.finanzas.compras.pagos()).data,
  });
}

export function useRegistrarPagoProveedor() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.finanzas.compras.registrarPago(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useCobranzasFin(filtros: {
  desde?: string;
  hasta?: string;
  search?: string;
  estado?: string;
  page?: number;
  limit?: number;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'cobranzas', filtros],
    enabled,
    queryFn: () => api.finanzas.cobranzas.list(filtros),
  });
}

export function useCrearCobranza() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.cobranzas.crear(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useAnularCobranza() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.cobranzas.anular(id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useProveedores(search = '') {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'proveedores', search],
    enabled,
    queryFn: () => api.finanzas.proveedores.list({ search, limit: 100 }),
  });
}

export function useCrearProveedor() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.proveedores.crear(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useInventario(search = '') {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'inventario', search],
    enabled,
    queryFn: () => api.finanzas.inventario.list({ search, limit: 200 }),
  });
}

export function useKardex(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'kardex', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.inventario.kardex(id!)).data,
  });
}

export function useAjusteInventario() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { productoId: string; cantidad: number; motivo: string }) =>
      api.finanzas.inventario.ajuste(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useBancos() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'bancos'],
    enabled,
    queryFn: async () => (await api.finanzas.banca.cuentas()).data,
  });
}

export function useMovimientosBancarios(filtros: {
  cuentaId?: string;
  desde?: string;
  hasta?: string;
  page?: number;
  limit?: number;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'movimientos', filtros],
    enabled,
    queryFn: () => api.finanzas.banca.movimientos(filtros),
  });
}

export function useCrearCuentaBancaria() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.banca.crearCuenta(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useTransaccionBancaria() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.banca.transaccion(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useConciliar() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.banca.conciliar(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useCuentasContables() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'cuentas'],
    enabled,
    queryFn: async () => (await api.finanzas.contabilidad.cuentas()).data,
  });
}

export function useCrearCuentaContable() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.finanzas.contabilidad.crearCuenta(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useAsientos(filtros: {
  desde?: string;
  hasta?: string;
  origen?: string;
  estado?: string;
  page?: number;
  limit?: number;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'asientos', filtros],
    enabled,
    queryFn: () => api.finanzas.contabilidad.asientos(filtros),
  });
}

export function useAsientoDetalle(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'asiento', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.contabilidad.asiento(id!)).data,
  });
}

export function useCrearAsiento() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.finanzas.contabilidad.crearAsiento(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useAnularAsiento() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.contabilidad.anularAsiento(id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useBalance(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'balance', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.contabilidad.balance({ desde, hasta }),
  });
}

export function useMayor(cuentaId?: string, desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'mayor', cuentaId, desde, hasta],
    enabled: enabled && Boolean(cuentaId),
    queryFn: async () =>
      (await api.finanzas.contabilidad.mayor({ cuentaId: cuentaId!, desde, hasta })).data,
  });
}

export function usePeriodos() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'periodos'],
    enabled,
    queryFn: async () => (await api.finanzas.contabilidad.periodos()).data,
  });
}

export function useCerrarPeriodo() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (id: string) => api.finanzas.contabilidad.cerrarPeriodo(id),
    onSuccess: () => void invalidate(),
  });
}

export function useMapeos() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'mapeos'],
    enabled,
    queryFn: async () => (await api.finanzas.contabilidad.mapeos()).data,
  });
}

export function useActualizarMapeo() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { clave: string; cuentaId: string }) =>
      api.finanzas.contabilidad.actualizarMapeo(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useCorrelativos() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'correlativos'],
    enabled,
    queryFn: async () => (await api.finanzas.contabilidad.correlativos()).data,
  });
}

export function useActualizarCorrelativo() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { siguienteNumero: number; password: string }) =>
      api.finanzas.contabilidad.actualizarCorrelativo(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useNotasList(params: {
  tipo?: 'CREDITO' | 'DEBITO';
  desde?: string;
  hasta?: string;
  limit?: number;
} = {}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'notas', params],
    enabled,
    queryFn: async () => (await api.finanzas.notas.list(params)).data,
  });
}

export function useComprasNotas(params: {
  tipo?: 'CREDITO' | 'DEBITO';
  desde?: string;
  hasta?: string;
  search?: string;
} = {}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'compras-notas', params],
    enabled,
    queryFn: () => api.finanzas.compras.notas(params),
  });
}

export function useRetencionesPanel(params: {
  desde?: string;
  hasta?: string;
  origen?: 'CLIENTE' | 'PROVEEDOR';
  incluirAnuladas?: boolean;
}) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'retenciones', params],
    enabled,
    queryFn: () => api.finanzas.retenciones.list(params),
  });
}

export function useAnularRetencion() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({
      origen,
      id,
      motivo,
    }: {
      origen: 'CLIENTE' | 'PROVEEDOR';
      id: string;
      motivo: string;
    }) => api.finanzas.retenciones.anular(origen, id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useEnviarRetencionTfhka() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (id: string) => api.finanzas.retenciones.enviar(id),
    onSuccess: () => void invalidate(),
  });
}

export function useLibroVentas(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'libro-ventas', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.libroVentas({ desde, hasta }),
  });
}

export function useLibroCompras(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'libro-compras', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.libroCompras({ desde, hasta }),
  });
}

export function useRetenciones(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'retenciones', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.retenciones({ desde, hasta }),
  });
}

export function useRetencionesVentas(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'retenciones-ventas', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.retencionesVentas({ desde, hasta }),
  });
}

export function useBalanceComprobacion(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'balance-comprobacion', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.balanceComprobacion({ desde, hasta }),
  });
}

export function useEstadoResultados(desde?: string, hasta?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'estado-resultados', desde, hasta],
    enabled,
    queryFn: () => api.finanzas.reportes.estadoResultados({ desde, hasta }),
  });
}

export { useFinanceAuth };

export function useAnularCompra() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.compras.anular(id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useAnularPagoProveedor() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.compras.anularPago(id, motivo),
    onSuccess: () => void invalidate(),
  });
}

export function useProyectos() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'proyectos'],
    enabled,
    queryFn: async () => (await api.finanzas.proyectos.list()).data,
  });
}

export function useCrearProyecto() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.proyectos.crear(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useProyectoDetalle(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'proyecto', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.proyectos.detail(id ?? '')).data,
  });
}

export function useNotas(facturaId?: string) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'notas', facturaId ?? 'todas'],
    enabled,
    queryFn: async () => (await api.finanzas.notas.list({ facturaId })).data,
  });
}

export function useCrearNota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.notas.crear(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fin', 'facturas'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'factura'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'notas'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'inventario'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-fin'] });
    },
  });
}

export function useAnularNota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api.finanzas.notas.anular(id, motivo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fin', 'facturas'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'factura'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'notas'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'inventario'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-fin'] });
    },
  });
}

export function useEmitirFiscal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      modo,
    }: {
      id: string;
      modo?: 'LOCAL' | 'TFHKA';
    }) => api.finanzas.facturas.fiscal(id, { modo }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fin', 'factura'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'facturas'] });
    },
  });
}

export function useEmitirFiscalNota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.finanzas.notas.fiscal(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fin', 'factura'] });
      void queryClient.invalidateQueries({ queryKey: ['fin', 'notas'] });
    },
  });
}

export function useProveedorResumen(id: string | null) {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'proveedor', 'resumen', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.finanzas.proveedores.resumen(id ?? '')).data,
  });
}

export function useProductos(search = '') {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'productos', search],
    enabled,
    queryFn: () => api.productos.list({ search, limit: 100 }),
  });
}

export function useCrearProducto() {
  const invalidate = useInvalidateFinance();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.productos.create(payload),
    onSuccess: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

export function useCatalogosProducto() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'catalogos-producto'],
    enabled,
    queryFn: async () => (await api.catalogos.productos()).data,
  });
}

export function useCrearCategoriaProducto() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { nombre: string; descripcion?: string }) =>
      api.catalogos.crearCategoria(payload),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useCrearUnidadMedida() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { codigo: string; nombre: string; permiteDecimales: boolean }) =>
      api.catalogos.crearUnidad(payload),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useEstadoCategoriaProducto() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { id: string; activo: boolean }) =>
      api.catalogos.estadoCategoria(payload.id, payload.activo),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useEstadoUnidadMedida() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: (payload: { id: string; activo: boolean }) =>
      api.catalogos.estadoUnidad(payload.id, payload.activo),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useSucursales() {
  const { enabled } = useFinanceAuth();
  return useQuery({
    queryKey: ['fin', 'sucursales'],
    enabled,
    queryFn: async () => (await api.sucursales.list()).data,
  });
}

export function useCrearSucursal() {
  const invalidate = useInvalidateFinance();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { codigo: string; nombre: string; direccion?: string }) =>
      api.sucursales.create(payload),
    onSuccess: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}
